import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { uploadFile } from "../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { fmtFCFA, fmtDate, statusBadgeClass, ROLES } from "../lib/format";
import { Paperclip, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";

export function StatusPill({ status, className = "", testId }) {
  const label = (status || "").replace(/(^|\s)\S/g, (t) => t.toUpperCase());
  return (
    <span data-testid={testId}
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusBadgeClass(status)} ${className}`}>
      {label || "—"}
    </span>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 pb-5 border-b border-border">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

const KPI_TONES = [
  ["emerald", "from-emerald-400 to-emerald-600"],
  ["green", "from-emerald-400 to-emerald-600"],
  ["blue", "from-blue-400 to-blue-600"],
  ["amber", "from-amber-400 to-amber-500"],
  ["sky", "from-sky-400 to-sky-600"],
  ["red", "from-red-400 to-red-600"],
];

export function KpiCard({ title, icon: Icon, children, accent = "text-foreground", testId }) {
  const bar = (KPI_TONES.find(([k]) => accent.includes(k)) || [null, "from-[#1E3A8A] to-[#0284C7]"])[1];
  return (
    <div data-testid={testId}
      className="group relative overflow-hidden bg-card rounded-xl border border-border shadow-premium card-hover">
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${bar}`} />
      <div className="p-5 pt-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
            <div className={`mt-2 text-2xl font-extrabold font-mono-num tracking-tight truncate ${accent}`}>{children}</div>
          </div>
          {Icon && (
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#0284C7] flex items-center justify-center text-white shrink-0 shadow-sm transition-transform duration-200 group-hover:scale-105 group-hover:-rotate-3">
              <Icon className="h-5 w-5" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, description, text = "No records yet.", action = null }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="h-14 w-14 rounded-2xl bg-muted border border-border flex items-center justify-center text-muted-foreground/60 mb-4">
        <Icon className="h-7 w-7" />
      </div>
      {title && <p className="text-base font-semibold text-foreground">{title}</p>}
      <p className="text-sm text-muted-foreground mt-1">{description || text}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Confirm({ onConfirm, title = "Are you sure?", description, trigger, actionLabel = "Delete" }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="confirm-action" onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700">{actionLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function FileField({ onUploaded, label = "Attach receipt / document", testId = "file-input" }) {
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadFile(file);
      setName(res.filename);
      onUploaded(res.file_id, res.filename);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
  };
  return (
    <label className="flex items-center gap-2 text-sm text-slate-600 border border-dashed border-slate-300 rounded-lg px-3 py-2.5 cursor-pointer hover:bg-slate-50">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
      <span className="truncate">{name || label}</span>
      <input type="file" className="hidden" data-testid={testId} onChange={handle}
        accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.csv,.txt" />
    </label>
  );
}

export function useBrand() {
  const [brand, setBrand] = useState({ name: "SilasTech Enterprise", logo_url: "" });
  useEffect(() => {
    api.get("/companies").then(({ data }) => {
      const active = localStorage.getItem("stems_company");
      const c = data.find((x) => x.id === active) || data[0];
      if (c) setBrand({ name: c.name || "SilasTech Enterprise", logo_url: c.logo_url || "" });
    }).catch(() => {});
  }, []);
  return brand;
}

export function useMeta() {
  const [meta, setMeta] = useState({
    roles: [], payment_purposes: [], project_statuses: [], project_categories: [],
  });
  useEffect(() => {
    api.get("/meta/config").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);
  return meta;
}

export function TxFilters({ projects = [], filters, setFilters, testPrefix = "filter" }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-4 flex flex-wrap items-end gap-3">
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">From</Label>
        <Input type="date" className="h-9 w-40" data-testid={`${testPrefix}-from`}
          value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">To</Label>
        <Input type="date" className="h-9 w-40" data-testid={`${testPrefix}-to`}
          value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Project</Label>
        <Select value={filters.project_id} onValueChange={(v) => set("project_id", v)}>
          <SelectTrigger className="h-9 w-56" data-testid={`${testPrefix}-project`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" className="h-9" data-testid={`${testPrefix}-reset`}
        onClick={() => setFilters({ date_from: "", date_to: "", project_id: "all" })}>Reset</Button>
    </div>
  );
}
